import React, { useState } from 'react';
import { Mail, Send, ArrowLeft, MessageSquare, Loader2, CheckCircle, LogIn, UserPlus, Lock } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';

const SUPPORT_EMAIL = 'support@quizessa.com';
const MAX_CHARS = 2000;

// Pre-fill the subject for known deep-links (e.g. the auto-split modal's "custom plan" CTA).
//
// COS-G C4 — 'Custom plan request' SE QUEDA EN INGLÉS. FIRMA DE CARLOS. No es un
// olvido de i18n ni una cadena pendiente de traducir: es el ASUNTO del correo que
// llega a soporte, y soporte lo lee en inglés. Que nadie lo "arregle".
const SUBJECT_PRESETS = {
  'custom-plan': 'Custom plan request',
};

// COS-G C3 — EL `detail` CRUDO DEL BACKEND DEJA DE SER EL AVISO.
//
// La pantalla pintaba `err.response?.data?.detail` tal cual: prosa que escribe el
// backend, SIEMPRE en inglés (`routes/support.py:34, 43` y el del rate limit), con
// la app en español. `/support/contact` no manda códigos clasificables —sus tres
// rechazos son `detail` de TEXTO—, así que el código del mapa es el HTTP.
//
// FALLBACK HONESTO, en dos escalones: un status que no esté aquí conserva el texto
// crudo del servidor (información real, aunque en inglés) y sólo si no vino texto
// cae el respaldo propio de la pantalla. Nunca se inventa un motivo.
const SEND_ERROR_KEYS = {
  403: 'support.toasts.send_guest_blocked',      // support.py:34 — invitado
  429: 'support.toasts.send_rate_limited',       // 5/usuario/día + 10/IP/día
  400: 'support.toasts.message_too_long',        // support.py:43 — único 400 del endpoint
};

export default function ContactPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // COS-21 — la pantalla salía 100% en inglés incluso con la app en español (el
  // S-3 heredado: nunca pasó por el sistema i18n). Namespace `common`: es el que
  // ya carga siempre (va en `ns` y es defaultNS, así que no hace falta registrar
  // uno nuevo ni gatear la carga) y el que ya tenía el vocabulario de soporte
  // (`nav.support`, `footer.contact_support`, `footer.support_email`). Los
  // literales que common YA tenía se reusan tal cual — "Back" (`back`),
  // "Log in" (`nav.log_in`), "Create Account" (`nav.create_account`) — cero
  // claves duplicadas.
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const isGuest = user?.is_guest;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: SUBJECT_PRESETS[searchParams.get('subject')] || '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error(t('support.toasts.fill_all_fields'));
      return;
    }
    if (form.message.length > MAX_CHARS) {
      toast.error(t('support.toasts.message_too_long', { max: MAX_CHARS }));
      return;
    }
    setSending(true);
    try {
      // Send only subject + message — the backend derives name/email from the authenticated
      // session (anti-spoof), so the locked Name/Email fields are display-only.
      await api.post('/support/contact', { subject: form.subject, message: form.message });
      setSent(true);
      toast.success(t('support.toasts.sent'));
    } catch (err) {
      // COS-G C3 — código → llave i18n, con el crudo como respaldo (ver SEND_ERROR_KEYS).
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const key = SEND_ERROR_KEYS[status];
      toast.error(
        key ? t(key, { max: MAX_CHARS })
          : (typeof detail === 'string' && detail) || t('support.toasts.send_failed')
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 animate-fade-in-up" data-testid="contact-success">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">{t('support.success.title')}</h2>
        <p className="text-[#a0a0a0] mb-8">
          <Trans i18nKey="support.success.body" t={t} values={{ email: form.email }} components={{ hl: <span key="hl" className="text-white" /> }} />
        </p>
        <Button onClick={() => navigate('/dashboard')} className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl px-6" data-testid="back-to-dashboard-btn">
          {t('support.success.back_btn')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in-up" data-testid="contact-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[#a0a0a0] hover:text-white transition-colors mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> {t('back')}
      </button>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">{t('support.title')}</h1>
        <p className="text-[#a0a0a0]">{t('support.subtitle')}</p>
      </div>

      {/* Direct email option */}
      <div className="glass-card rounded-[16px] p-5 mb-6 flex items-center gap-4" data-testid="direct-email-card">
        <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
          <Mail className="w-5 h-5 text-[#FF6B00]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#a0a0a0] mb-0.5">{t('support.direct_email_prompt')}</p>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm font-medium text-[#FF6B00] hover:underline" data-testid="support-email-link">
            {SUPPORT_EMAIL}
          </a>
        </div>
      </div>

      {/* Contact form */}
      {isGuest ? (
        <div className="glass-card rounded-[16px] p-8 text-center space-y-4" data-testid="guest-form-block">
          <MessageSquare className="w-10 h-10 text-[#333] mx-auto" />
          <h2 className="text-base font-semibold text-white">{t('support.guest.title')}</h2>
          <p className="text-sm text-[#a0a0a0]">{t('support.guest.body')}</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => navigate('/login')} className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-10 text-sm" data-testid="guest-login-btn">
              <LogIn className="w-4 h-4 mr-2" /> {t('nav.log_in')}
            </Button>
            <Button onClick={() => navigate('/signup')} variant="outline" className="border-[#333] text-[#aaa] hover:text-white rounded-xl h-10 text-sm" data-testid="guest-signup-btn">
              <UserPlus className="w-4 h-4 mr-2" /> {t('nav.create_account')}
            </Button>
          </div>
        </div>
      ) : (
      <div className="glass-card rounded-[16px] p-6" data-testid="contact-form-card">
        <div className="flex items-center gap-2 mb-5">
          <MessageSquare className="w-4 h-4 text-[#FF6B00]" />
          <h2 className="text-sm font-semibold text-white">{t('support.form.heading')}</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name + Email are locked to the signed-in account — display-only. The backend derives
              them from the session, so editing them here would have no effect (anti-spoof). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1 text-xs text-[#a0a0a0] mb-1.5">
                {t('support.form.name_label')} <Lock className="w-3 h-3 text-[#999]" /> <span className="text-[#999]">{t('support.form.from_account')}</span>
              </label>
              <input type="text" value={form.name} readOnly tabIndex={-1} aria-readonly="true"
                className="w-full bg-[#101010] border border-[#1f1f1f] rounded-xl px-3 py-2.5 text-base sm:text-sm text-[#9a9a9a] cursor-not-allowed focus:outline-none"
                data-testid="contact-name-input" />
            </div>
            <div>
              <label className="flex items-center gap-1 text-xs text-[#a0a0a0] mb-1.5">
                {t('support.form.email_label')} <Lock className="w-3 h-3 text-[#999]" /> <span className="text-[#999]">{t('support.form.from_account')}</span>
              </label>
              <input type="email" value={form.email} readOnly tabIndex={-1} aria-readonly="true"
                className="w-full bg-[#101010] border border-[#1f1f1f] rounded-xl px-3 py-2.5 text-base sm:text-sm text-[#9a9a9a] cursor-not-allowed focus:outline-none"
                data-testid="contact-email-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#a0a0a0] mb-1.5">{t('support.form.subject_label')}</label>
            <input type="text" value={form.subject} required
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full bg-[#181818] border border-[#262626] rounded-xl px-3 py-2.5 text-base sm:text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#FF6B00]/50 transition-colors"
              placeholder={t('support.form.subject_placeholder')} data-testid="contact-subject-input" />
          </div>
          <div>
            <label className="block text-xs text-[#a0a0a0] mb-1.5">{t('support.form.message_label')}</label>
            <textarea value={form.message} required rows={5}
              onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, MAX_CHARS) })}
              className="w-full bg-[#181818] border border-[#262626] rounded-xl px-3 py-2.5 text-base sm:text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#FF6B00]/50 transition-colors resize-none"
              placeholder={t('support.form.message_placeholder')} data-testid="contact-message-input" />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-xs text-[#999]">{t('support.form.reassurance')}</span>
              {/* La cuenta va por interpolación con `chars`, NO con `count`:
                  `count` activaría el plural de i18next y el inglés de hoy —la
                  fuente— es una sola forma ("1 characters remaining"). El
                  español la calca en estructura por la misma razón. */}
              <span className={`text-xs ${(MAX_CHARS - form.message.length) < 100 ? 'text-[#FF6B00]' : 'text-[#999]'}`} data-testid="contact-char-count">
                {/* COS-G C1 — decía "1 caracteres restantes". La llave pasa a plural de
                    i18next (`_one`/`_other`), que necesita `count`; el plural es el mismo
                    de siempre y sólo nace el singular. */}
                {t('support.form.chars_remaining', { count: MAX_CHARS - form.message.length })}
              </span>
            </div>
          </div>
          <Button type="submit" disabled={sending}
            className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-11 text-sm font-semibold"
            data-testid="contact-submit-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {sending ? t('support.form.sending_btn') : t('support.form.submit_btn')}
          </Button>
        </form>
      </div>
      )}
    </div>
  );
}
